import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Mail, MapPin, Twitter, Linkedin, Youtube, ArrowUpRight } from "lucide-react";

export default function Footer() {
  const { t, lang } = useLang();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await api.post("/newsletter", { email, lang });
      toast.success(t.contact.subscribed);
      setEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer className="bg-paper text-ink mt-24 border-t border-slate200" data-testid="site-footer">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16 grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 bg-slate500 text-white grid place-items-center text-sm font-bold">M</div>
            <div>
              <div className="heading-serif text-2xl text-ink">{t.site_name}</div>
              <div className="label-eyebrow text-slate600 mt-1">MESC-CE</div>
            </div>
          </div>
          <p className="text-[14px] leading-relaxed text-slate600 max-w-md">
            {t.home.hero_sub}
          </p>
        </div>

        <div className="lg:col-span-3">
          <div className="label-eyebrow text-slate500 mb-3">{t.contact.address_h}</div>
          <div className="text-[13px] text-slate700 leading-relaxed flex items-start gap-2">
            <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
            <span>Brussels · Geneva · Paris<br />Hybrid research network</span>
          </div>
          <div className="label-eyebrow text-slate500 mt-6 mb-3">{t.contact.email_h}</div>
          <a
            href="mailto:contact@mesc-ce.org"
            className="text-[13px] text-ink hover:opacity-80 link-underline inline-flex items-center gap-1"
          >
            <Mail className="w-4 h-4" /> contact@mesc-ce.org
          </a>
          <div className="label-eyebrow text-slate500 mt-6 mb-3">{t.contact.social_h}</div>
          <div className="flex items-center gap-3">
            <a href="#" aria-label="twitter" className="p-2 border border-slate300 hover:bg-ink hover:text-white hover:border-ink transition-colors" data-testid="social-twitter">
              <Twitter className="w-4 h-4" />
            </a>
            <a href="#" aria-label="linkedin" className="p-2 border border-slate300 hover:bg-ink hover:text-white hover:border-ink transition-colors" data-testid="social-linkedin">
              <Linkedin className="w-4 h-4" />
            </a>
            <a href="#" aria-label="youtube" className="p-2 border border-slate300 hover:bg-ink hover:text-white hover:border-ink transition-colors" data-testid="social-youtube">
              <Youtube className="w-4 h-4" />
            </a>
          </div>
        </div>

        <div className="lg:col-span-4">
          <div className="label-eyebrow text-slate500 mb-3">{t.contact.newsletter_h}</div>
          <p className="text-[13px] text-slate600 mb-4">{t.contact.newsletter_sub}</p>
          <form onSubmit={subscribe} className="flex border border-slate300 bg-white" data-testid="footer-newsletter-form">
            <input
              data-testid="footer-newsletter-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t.contact.newsletter_placeholder}
              className="flex-1 bg-transparent px-3 h-10 text-[13px] placeholder:text-slate400 outline-none text-ink"
            />
            <button
              type="submit"
              disabled={busy}
              data-testid="footer-newsletter-submit"
              className="px-4 bg-ink text-white text-[12px] font-semibold tracking-wider uppercase hover:bg-ink/90 disabled:opacity-50 inline-flex items-center gap-1"
            >
              {t.contact.subscribe} <ArrowUpRight className="w-3.5 h-3.5" />
            </button>
          </form>
        </div>
      </div>

      <div className="border-t border-slate300/60">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-5 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="text-[11px] text-slate500 tracking-wider uppercase">
            © {new Date().getFullYear()} MESC-CE · All rights reserved
          </div>
          <div className="flex gap-5 text-[11px] text-slate500">
            <Link to="/about" className="hover:text-ink">{t.nav.about}</Link>
            <Link to="/contact" className="hover:text-ink">{t.nav.contact}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
